/**
 * -----------------------------------------------------------------------------
 * User ACL Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the user-specific ACL form.
 */

import React, {
  FC,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  useOrganizationRoleCategoryListQuery,
  useOrganizationRoleCategoryACLListQuery,
} from "../../../api/network/adminApiServices";
import {
  ACLAccess,
  CategoryACL,
  TLegacyUserRoleACL,
  TRoleACLCategory,
} from "../../../api/types/UserRole";
import {
  AdminFormFieldIntermediateCheckbox,
  AdminFormFieldRadio,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";

import { useLoggedInUserData } from "../../../utils/user";
import Accordian from "../../../components/accordian";
import LoadingAnimation from "../../../assets/svg/loadingAnimation.svg";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../../constants/constants";

interface UserAclFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
}

interface UserCategoryAccessControlProps {
  category: TRoleACLCategory;
  role: string;
  accountId: string;
  values: any;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  userCanEdit: boolean;
  ref?: Ref<any>;
}

export interface UserCategoryAccessControlRef {
  onResetClick: () => void;
}

const UserCategoryAccessControl = forwardRef<
  UserCategoryAccessControlRef,
  UserCategoryAccessControlProps
>(({ category, role, accountId, values, formikSetValue, userCanEdit }, ref) => {
  const { data: dataCategoryAcls, isFetching: isFetchingACLList } =
    useOrganizationRoleCategoryACLListQuery({
      category: category.id,
      role: role, //  role
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
      user_id: values.user_id,
    });

  const { results } = dataCategoryAcls || {};
  console.log("user acl category : ", results);

  // Get all type of access levels from all ACLs
  const accessLevels = new Set<number>();
  results?.map((acl) =>
    acl.acl_access.map((item) => {
      accessLevels.add(item.access_level);
    })
  );

  const [resultState, setResultState] = useState<CategoryACL[]>([]);

  useEffect(() => {
    if (results) setResultState(results);
  }, [results]);

  const onResetClick = () => {
    let user_acls: TLegacyUserRoleACL[] = [...values.user_acls];
    results?.forEach((categoryACL: CategoryACL) => {
      const savedUserAclIndex = user_acls?.findIndex(
        (legacyUserAcl: TLegacyUserRoleACL) =>
          legacyUserAcl.aclid.toLowerCase() === categoryACL.name.toLowerCase()
      );
      const defaultAccessLevel = categoryACL.acl_access
        .map((item) => (item.is_default ? item.access_level : null))
        .filter((item) => item !== null)[0] as 0 | 1 | 2 | 3;
      const modifyACLObject = {
        accountid: accountId,
        roleid: role,
        aclid: categoryACL.name,
        accesslevel: defaultAccessLevel,
        description: categoryACL.description,
      };
      setResultState((prev) => {
        return prev.map((acl) => {
          if (acl.id === categoryACL.id) {
            // Update access_level for the matching ACL
            return {
              ...acl,
              user_access_level: defaultAccessLevel,
              access_level: defaultAccessLevel,
            };
          }
          return acl;
        });
      });
      if (savedUserAclIndex > -1) {
        user_acls[savedUserAclIndex] = modifyACLObject;
      } else {
        user_acls.push(modifyACLObject);
      }
    });
    formikSetValue("user_acls", user_acls);
  };
  /**
   * Expose the `onClick` function to the parent component.
   */
  useImperativeHandle(ref, () => ({
    onResetClick,
  }));

  const sortedAccessLevels = Array.from(accessLevels).sort();

  interface SelectAllProps {
    values: any;
    acls: CategoryACL[] | undefined;
  }

  const SelectAllOption: FC<SelectAllProps> = ({ acls, values }) => {
    if (!acls) return null;
    // Get saved access level of the acls
    let isSameAccessLevelSaved = false;
    let isIntermediateSelected = false;
    const savedSelectedLevels = new Set<number>();

    acls.map((acl) => {
      const aclAccessLevel = values?.user_acls?.find(
        (user_acl: TLegacyUserRoleACL) =>
          user_acl.aclid.toLowerCase() === acl.name.toLowerCase()
      );
      if (aclAccessLevel) {
        savedSelectedLevels.add(aclAccessLevel.accesslevel);
      } else {
        // Priority: user_access_level > access_level > default
        const currentLevel = acl.user_access_level ?? acl.access_level;
        if (currentLevel !== undefined && currentLevel !== null) {
          savedSelectedLevels.add(currentLevel);
        } else {
          acl.acl_access.map((item) => {
            if (item.is_default) {
              savedSelectedLevels.add(item.access_level);
            }
          });
        }
      }
      return null;
    });

    if (savedSelectedLevels.size === 1) {
      isSameAccessLevelSaved = true;
    } else if (savedSelectedLevels.size > 1) {
      isIntermediateSelected = true;
    }

    return (
      <div
        className="px-5 flex-grow flex items-center col-span-12 border-b-2 py-3 h-15"
        key={category.id}
      >
        <label className="min-w-64 max-w-64 text-sm" />
        {sortedAccessLevels.map((item: number, i: number) => {
          return (
            <div className="flex justify-start row-start" key={i}>
              <AdminFormFieldIntermediateCheckbox
                disabled={!userCanEdit}
                key={`${acls[0].name.toString()}_${item}`}
                label={"Select All"}
                name={`${acls[0].name.toString()}_${item}`}
                id={`${acls[0].name.toString()}_${item}`}
                type="radio"
                value={item}
                customWrapperClass="w-36 flex items-center bg-white py-2"
                customLabelTextClass="ml-2 text-sm font-bold"
                disabledClass="disabled:opacity-50"
                checked={
                  isSameAccessLevelSaved && savedSelectedLevels.has(item)
                }
                intermediate={
                  isIntermediateSelected && savedSelectedLevels.has(item)
                }
                onChange={(e) => {
                  if (e.target.value) {
                    let user_acls: TLegacyUserRoleACL[] = [...values.user_acls];
                    results?.map((categoryACLItem: CategoryACL, i: number) => {
                      let isLevelExistForAcl = false;
                      categoryACLItem.acl_access.map((level) =>
                        level.access_level === item
                          ? (isLevelExistForAcl = true)
                          : null
                      );
                      if (!isLevelExistForAcl) return null;

                      const savedUserAclIndex = user_acls?.findIndex(
                        (legacyUserAcl: TLegacyUserRoleACL) =>
                          legacyUserAcl.aclid.toLowerCase() ===
                          categoryACLItem.name.toLowerCase()
                      );
                      const modifyACLObject = {
                        accountid: accountId,
                        roleid: role,
                        aclid: categoryACLItem.name,
                        accesslevel: item as 0 | 1 | 2 | 3,
                        description: categoryACLItem.description,
                      };
                      setResultState((prev) => {
                        return prev.map((acl) => {
                          if (acl.id === categoryACLItem.id) {
                            return {
                              ...acl,
                              user_access_level: item as 0 | 1 | 2 | 3,
                              access_level: item as 0 | 1 | 2 | 3,
                            };
                          }
                          return acl;
                        });
                      });
                      if (savedUserAclIndex > -1) {
                        user_acls[savedUserAclIndex] = modifyACLObject;
                      } else {
                        user_acls.push(modifyACLObject);
                      }
                    });
                    formikSetValue("user_acls", user_acls);
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {isFetchingACLList && (
        <div className="col-span-12 h-20 flex items-center justify-center bg-opacity-90">
          <img
            src={LoadingAnimation}
            alt="Loading animation"
            className="w-12 h-12"
          />
        </div>
      )}
      <SelectAllOption
        acls={resultState && resultState?.length > 0 ? resultState : undefined}
        values={values}
      />
      <div className="max-h-72 overflow-auto">
        {resultState?.map((categoryACL: CategoryACL, i: number) => {
          const savedUserAclIndex = values?.user_acls?.findIndex(
            (legacyUserAcl: TLegacyUserRoleACL) =>
              legacyUserAcl.aclid.toLowerCase() ===
              categoryACL.name.toLowerCase()
          );
          const savedUserAclItem =
            savedUserAclIndex > -1
              ? values?.user_acls[savedUserAclIndex]
              : null;

          // Priority: user_access_level > access_level
          const currentAccessLevel =
            categoryACL.user_access_level ?? categoryACL.access_level;

          return (
            <div
              className="px-5 flex-grow flex items-center col-span-12 border-b-2 py-3 h-15 "
              key={categoryACL.id}
            >
              <label className="min-w-64 max-w-64 text-sm flex flex-col">
                <span>{categoryACL.description}</span>
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                  <span className="text-heading-gray888 text-xs">
                    {categoryACL.name}
                    {` (${currentAccessLevel})`}
                  </span>
                )}
              </label>
              <div className="flex justify-start row-start grow">
                {sortedAccessLevels.map((accessLevel: number) => {
                  const item = categoryACL.acl_access.find(
                    (aclItem: ACLAccess) =>
                      aclItem.access_level === accessLevel ? aclItem : null
                  );
                  if (item) {
                    return (
                      <AdminFormFieldRadio
                        disabled={!userCanEdit}
                        key={`${categoryACL.name.toString()}_${item.access_level}_${item.id}`}
                        label={item.custom_access_level_name}
                        name={item.id.toString()}
                        id={item.id.toString()}
                        subText={item.is_default ? "(Default)" : undefined}
                        error="hi"
                        type="radio"
                        value={item.access_level}
                        customWrapperClass="w-36 col-span-2 flex items-center bg-white py-2 rounded-xl"
                        customLabelTextClass="ml-2 text-sm font-bold"
                        disabledClass="disabled:opacity-50"
                        checked={item.access_level === currentAccessLevel}
                        onChange={(e) => {
                          if (e.target.value) {
                            const newAccessLevel = parseInt(e.target.value) as
                              | 0
                              | 1
                              | 2
                              | 3;
                            const modifyACLObject = {
                              accountid: accountId,
                              roleid: role,
                              aclid: categoryACL.name,
                              accesslevel: newAccessLevel,
                              description: categoryACL.description,
                            };
                            setResultState((prev) => {
                              return prev.map((acl) => {
                                if (acl.id === categoryACL.id) {
                                  return {
                                    ...acl,
                                    user_access_level: newAccessLevel,
                                    access_level: newAccessLevel,
                                  };
                                }
                                return acl;
                              });
                            });
                            let user_acls = [...values.user_acls];
                            if (savedUserAclIndex > -1) {
                              user_acls[savedUserAclIndex] = modifyACLObject;
                            } else {
                              user_acls.push(modifyACLObject);
                            }
                            formikSetValue("user_acls", user_acls);
                          }
                        }}
                      />
                    );
                  } else {
                    return (
                      <div
                        className="w-36 inline-flex items-center bg-white py-2 self-center"
                        key={`empty_${accessLevel}`}
                      >
                        <span className="block text-center w-16 ml-5">-</span>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export const UserAccessControlAclForm: FC<UserAclFormProps> = ({
  values,
  errors,
  touched,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.users.detailsPage.form",
  });
  const categoryRef = useRef<UserCategoryAccessControlRef>(null);

  const { data: dataOrgRolesCategory, isFetching: isFetchingOrgRolesCategory } =
    useOrganizationRoleCategoryListQuery({
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    });
  const { results } = dataOrgRolesCategory || {};
  console.log("user role category : ", results);

  return (
    <div className="px-5 pt-4 pb-8 bg-white w-full overflow-y-auto">
      {isFetchingOrgRolesCategory && (
        <div className="col-span-12 h-20 flex items-center justify-center bg-opacity-90">
          <img
            src={LoadingAnimation}
            alt="Loading animation"
            className="w-12 h-12"
          />
        </div>
      )}
      {results?.map((item: TRoleACLCategory, index: number) => {
        return (
          <Accordian
            title={
              <div className="flex justify-between w-full">
                <span>{item.name}</span>
                <AdminFormFieldSubmit
                  type="button"
                  variant="danger"
                  label={t("reset_default")}
                  onClick={() => {
                    if (userCanEdit) {
                      categoryRef.current?.onResetClick();
                    }
                  }}
                  disabled={results.length === 0 || loadingData || !userCanEdit}
                  customWrapperClass="pr-20"
                />
              </div>
            }
            collapsible={true}
            key={item.id}
            isMultiClick={true}
          >
            <UserCategoryAccessControl
              accountId={thisUserOrganizationId || ""}
              category={item}
              role={values.default_acl_role}
              values={values}
              formikSetValue={formikSetValue}
              key={item.name}
              userCanEdit={userCanEdit}
              ref={categoryRef}
            />
          </Accordian>
        );
      })}
    </div>
  );
};
